import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import type { FeatureCollection, Point } from 'geojson';
import maplibregl from 'maplibre-gl';
import { TbMapPinFilled, TbRoute } from 'react-icons/tb';
import type { LayerProps, MapLayerMouseEvent, MapRef } from 'react-map-gl/maplibre';
import ReactMap, { Layer, Source } from 'react-map-gl/maplibre';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    createThemedMapStyle,
    ThemedMapControls,
    ThemedMapPopup,
} from '@/shared/components/data-visualization/maplibre';

import {
    createTradeFlowFeatureCollection,
    TradeFlow,
    TradeHub,
    TradeMapTheme,
    TradeMapView,
    TradeMode,
    TradeProjection,
} from './model';
import { calculateResponsiveGlobeZoom } from './viewport';

import 'maplibre-gl/dist/maplibre-gl.css';

const FLOW_LAYER_ID = 'global-trade-flows';
const HUB_LAYER_ID = 'global-trade-hubs';
const GLOBE_FIT_RESIZE_DELAY_MS = 160;
const GLOBE_FIT_PADDING_PX = 32;
const RESPONSIVE_GLOBE_MIN_ZOOM = -2;

interface TradeHubFeatureProperties {
    id: string;
    kind: TradeHub['kind'];
}

interface GlobalTradeMapProps {
    fitToContainer?: boolean;
    flows: TradeFlow[];
    hubs: TradeHub[];
    mapView: TradeMapView;
    mapTheme?: TradeMapTheme;
    projection: TradeProjection;
    resetView?: TradeMapView;
    resetRequest: number;
    selectedFlow?: TradeFlow;
    selectedHub?: TradeHub;
    showFlows: boolean;
    showHubs: boolean;
    onClearSelection: () => void;
    onSelectFlow: (flowId: string) => void;
    onSelectHub: (hubId: string) => void;
    onViewChange?: (mapView: Pick<TradeMapView, 'bearing' | 'latitude' | 'longitude' | 'pitch' | 'zoom'>) => void;
}

const GlobalTradeMap: FC<GlobalTradeMapProps> = ({
    fitToContainer = false,
    flows,
    hubs,
    mapView,
    mapTheme = 'adaptive',
    projection,
    resetView,
    resetRequest,
    selectedFlow,
    selectedHub,
    showFlows,
    showHubs,
    onClearSelection,
    onSelectFlow,
    onSelectHub,
    onViewChange,
}): JSX.Element => {
    const { resolvedThemeName, token } = useAntDesignTheme();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const handledResetRequestRef = useRef(0);
    const mapRef = useRef<MapRef | null>(null);
    const resolvedMapTheme = mapTheme === 'adaptive' ? (resolvedThemeName === 'dark' ? 'dark' : 'positron') : mapTheme;
    const mapStyle = `https://tiles.openfreemap.org/styles/${resolvedMapTheme}`;
    const flowCollection = useMemo(() => createTradeFlowFeatureCollection(flows, hubs), [flows, hubs]);
    const hubCollection = useMemo<FeatureCollection<Point, TradeHubFeatureProperties>>(
        () => ({
            features: hubs.map((hub) => ({
                geometry: {
                    coordinates: [hub.longitude, hub.latitude],
                    type: 'Point',
                },
                properties: {
                    id: hub.id,
                    kind: hub.kind,
                },
                type: 'Feature',
            })),
            type: 'FeatureCollection',
        }),
        [hubs],
    );
    const flowById = useMemo(() => new Map(flows.map((flow) => [flow.id, flow])), [flows]);
    const hubById = useMemo(() => new Map(hubs.map((hub) => [hub.id, hub])), [hubs]);
    const modeColor: Record<TradeMode, string> = {
        air: token.purple6,
        ocean: token.blue6,
        rail: token.gold6,
        road: token.cyan6,
    };
    const airportHubColor = token.purple6;
    const borderHubColor = token.cyan6;
    const inlandPortHubColor = token.gold6;
    const portHubColor = token.blue6;
    const flowLayer = useMemo<LayerProps>(
        () => ({
            id: FLOW_LAYER_ID,
            layout: {
                'line-cap': 'round',
                'line-join': 'round',
            },
            paint: {
                'line-color': [
                    'match',
                    ['get', 'mode'],
                    'air',
                    modeColor.air,
                    'rail',
                    modeColor.rail,
                    'road',
                    modeColor.road,
                    modeColor.ocean,
                ],
                'line-opacity': [
                    'case',
                    ['==', ['get', 'id'], selectedFlow?.id ?? ''],
                    0.95,
                    resolvedThemeName === 'dark' ? 0.68 : 0.58,
                ],
                'line-width': ['case', ['==', ['get', 'id'], selectedFlow?.id ?? ''], 4, 2],
            },
            source: FLOW_LAYER_ID,
            type: 'line',
        }),
        [modeColor.air, modeColor.ocean, modeColor.rail, modeColor.road, resolvedThemeName, selectedFlow?.id],
    );
    const hubLayer = useMemo<LayerProps>(
        () => ({
            id: HUB_LAYER_ID,
            paint: {
                'circle-color': [
                    'match',
                    ['get', 'kind'],
                    'airport',
                    airportHubColor,
                    'border',
                    borderHubColor,
                    'inland-port',
                    inlandPortHubColor,
                    portHubColor,
                ],
                'circle-opacity': 1,
                'circle-radius': ['case', ['==', ['get', 'id'], selectedHub?.id ?? ''], 8, 6],
                'circle-stroke-color': [
                    'case',
                    ['==', ['get', 'id'], selectedHub?.id ?? ''],
                    token.colorTextLightSolid,
                    token.colorBgBase,
                ],
                'circle-stroke-width': 2,
            },
            source: HUB_LAYER_ID,
            type: 'circle',
        }),
        [
            airportHubColor,
            borderHubColor,
            inlandPortHubColor,
            portHubColor,
            selectedHub?.id,
            token.colorBgBase,
            token.colorTextLightSolid,
        ],
    );

    const fitMapToContainer = useCallback(() => {
        const map = mapRef.current?.getMap();

        if (!map) {
            return;
        }

        map.resize();

        if (!fitToContainer || projection !== 'globe') {
            return;
        }

        const { clientHeight, clientWidth } = map.getContainer();

        if (!clientHeight || !clientWidth) {
            return;
        }

        map.jumpTo({
            bearing: mapView.bearing ?? 0,
            center: [mapView.longitude, mapView.latitude],
            pitch: mapView.pitch ?? 0,
            zoom: calculateResponsiveGlobeZoom({
                configuredZoom: mapView.zoom ?? 2.35,
                height: clientHeight,
                minZoom: RESPONSIVE_GLOBE_MIN_ZOOM,
                padding: GLOBE_FIT_PADDING_PX,
                width: clientWidth,
            }),
        });
    }, [fitToContainer, mapView.bearing, mapView.latitude, mapView.longitude, mapView.pitch, mapView.zoom, projection]);

    const resetMapPosition = useCallback(() => {
        const map = mapRef.current?.getMap();
        const nextView = resetView ?? mapView;

        if (!map) {
            return;
        }

        map.resize();

        const { clientHeight, clientWidth } = map.getContainer();
        const zoom =
            fitToContainer && projection === 'globe' && clientHeight && clientWidth
                ? calculateResponsiveGlobeZoom({
                      configuredZoom: nextView.zoom ?? 2.35,
                      height: clientHeight,
                      minZoom: RESPONSIVE_GLOBE_MIN_ZOOM,
                      padding: GLOBE_FIT_PADDING_PX,
                      width: clientWidth,
                  })
                : nextView.zoom;

        map.flyTo({
            bearing: nextView.bearing ?? 0,
            center: [nextView.longitude, nextView.latitude],
            duration: 650,
            pitch: nextView.pitch ?? 0,
            zoom,
        });
    }, [fitToContainer, mapView, projection, resetView]);

    useEffect(() => {
        const container = containerRef.current;

        if (!container) {
            return undefined;
        }

        let fitTimeout: ReturnType<typeof setTimeout> | undefined;
        const resizeObserver = new ResizeObserver(() => {
            if (fitTimeout) {
                clearTimeout(fitTimeout);
            }

            fitTimeout = setTimeout(fitMapToContainer, GLOBE_FIT_RESIZE_DELAY_MS);
        });

        resizeObserver.observe(container);

        return () => {
            resizeObserver.disconnect();

            if (fitTimeout) {
                clearTimeout(fitTimeout);
            }
        };
    }, [fitMapToContainer]);

    useEffect(() => {
        if (!resetRequest || handledResetRequestRef.current === resetRequest) {
            return;
        }

        handledResetRequestRef.current = resetRequest;
        resetMapPosition();
    }, [resetMapPosition, resetRequest]);

    const handleMapClick = (event: MapLayerMouseEvent) => {
        const hubId = event.features?.find((feature) => feature.layer.id === HUB_LAYER_ID)?.properties?.id;

        if (typeof hubId === 'string' && hubById.has(hubId)) {
            onSelectHub(hubId);
            return;
        }

        const flowId = event.features?.find((feature) => feature.layer.id === FLOW_LAYER_ID)?.properties?.id;

        if (typeof flowId === 'string' && flowById.has(flowId)) {
            onSelectFlow(flowId);
            return;
        }

        onClearSelection();
    };

    const selectedFlowOrigin = selectedFlow ? hubById.get(selectedFlow.originId) : undefined;
    const selectedFlowDestination = selectedFlow ? hubById.get(selectedFlow.destinationId) : undefined;
    const popupLongitude =
        selectedFlowOrigin && selectedFlowDestination
            ? (selectedFlowOrigin.longitude + selectedFlowDestination.longitude) / 2
            : selectedHub?.longitude;
    const popupLatitude =
        selectedFlowOrigin && selectedFlowDestination
            ? (selectedFlowOrigin.latitude + selectedFlowDestination.latitude) / 2
            : selectedHub?.latitude;

    return (
        <div
            className={`cc-themed-map${resolvedThemeName === 'dark' ? ' cc-themed-map--dark' : ''}`}
            ref={containerRef}
            style={{ height: '100%', minHeight: 0, overflow: 'hidden', width: '100%' }}
        >
            <ReactMap
                attributionControl={false}
                initialViewState={{
                    bearing: mapView.bearing ?? 0,
                    latitude: mapView.latitude,
                    longitude: mapView.longitude,
                    pitch: mapView.pitch ?? 0,
                    zoom: mapView.zoom,
                }}
                interactiveLayerIds={[...(showFlows ? [FLOW_LAYER_ID] : []), ...(showHubs ? [HUB_LAYER_ID] : [])]}
                mapLib={maplibregl}
                mapStyle={mapStyle}
                minZoom={fitToContainer ? RESPONSIVE_GLOBE_MIN_ZOOM : undefined}
                onClick={handleMapClick}
                onLoad={fitMapToContainer}
                onMouseEnter={(event) => {
                    if (event.features?.length) {
                        event.target.getCanvas().style.cursor = 'pointer';
                    }
                }}
                onMouseLeave={(event) => {
                    event.target.getCanvas().style.cursor = '';
                }}
                onMoveEnd={(event) =>
                    onViewChange?.({
                        bearing: event.viewState.bearing,
                        latitude: event.viewState.latitude,
                        longitude: event.viewState.longitude,
                        pitch: event.viewState.pitch,
                        zoom: event.viewState.zoom,
                    })
                }
                projection={projection}
                ref={mapRef}
                reuseMaps
                style={createThemedMapStyle(token, {
                    background: token.colorBgBase,
                    height: '100%',
                    width: '100%',
                })}
            >
                <ThemedMapControls mapRef={mapRef} onReset={resetMapPosition} />

                {showFlows ? (
                    <Source data={flowCollection} id={FLOW_LAYER_ID} type='geojson'>
                        <Layer {...flowLayer} />
                    </Source>
                ) : null}

                {showHubs ? (
                    <Source data={hubCollection} id={HUB_LAYER_ID} type='geojson'>
                        <Layer {...hubLayer} />
                    </Source>
                ) : null}

                {popupLatitude !== undefined && popupLongitude !== undefined ? (
                    <ThemedMapPopup
                        icon={selectedFlow ? <TbRoute /> : <TbMapPinFilled />}
                        items={
                            selectedFlow
                                ? [
                                      { label: 'Mode', value: selectedFlow.mode },
                                      { label: 'Category', value: selectedFlow.category },
                                  ]
                                : selectedHub
                                  ? [{ label: 'Type', value: selectedHub.kind }]
                                  : []
                        }
                        latitude={popupLatitude}
                        longitude={popupLongitude}
                        onClose={onClearSelection}
                        subtitle={
                            selectedFlow
                                ? `${selectedFlowOrigin?.country} → ${selectedFlowDestination?.country}`
                                : `${selectedHub?.country} · ${selectedHub?.region}`
                        }
                        title={selectedFlow?.label ?? selectedHub?.name}
                    />
                ) : null}
            </ReactMap>
        </div>
    );
};

export default GlobalTradeMap;
